import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

interface SidePanelContextValue {
  isOpen: boolean;
  title: string;
  content: ReactNode | null;
  open: (content: ReactNode, title?: string) => void;
  close: () => void;
}

const SidePanelContext = createContext<SidePanelContextValue | null>(null);

/**
 * Lightweight right-side drawer. Mirrors the webui `SidePanelContext` so an
 * analyst can open a contextual editor (e.g. detection details + actions)
 * without leaving the dashboard.
 */
export function SidePanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState<ReactNode | null>(null);

  const open = useCallback((newContent: ReactNode, newTitle = "") => {
    setContent(newContent);
    setTitle(newTitle);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <SidePanelContext.Provider value={{ isOpen, title, content, open, close }}>
      {children}
    </SidePanelContext.Provider>
  );
}

export function useSidePanel(): SidePanelContextValue {
  const ctx = useContext(SidePanelContext);
  if (!ctx) throw new Error("useSidePanel must be used inside SidePanelProvider");
  return ctx;
}
